import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  CreateNoticePayload,
  NoticeList,
  NoticeListQuery,
  TeacherNotice,
  UpdateNoticePayload,
} from '@/features/teacher/types/notices';

/**
 * Every call the notice board makes — the page and the overview card both.
 *
 * Its own file rather than more methods on `teacher.api.ts`, for the reason
 * homework has one: a feature's network layer that grows without bound is the
 * one nobody can find anything in (CLAUDE.md §6).
 *
 * No path carries an institute id or an academic year, and none carries who
 * posted the notice — the server takes all of that from the caller's token.
 */
export const noticesApi = {
  /**
   * The caller's own notice board, newest first.
   *
   * Every filter is applied by the server rather than by the browser, so a
   * notice further back than the newest hundred is still found: `onDate` is the
   * day a notice is *about*, and the rest narrow by class, subject and whether
   * it has been published.
   */
  list: (query: NoticeListQuery = {}) =>
    unwrap(
      apiClient.get<ApiSuccess<NoticeList>>('/institute/notices', {
        params: {
          ...(query.classSectionUuid && { classSectionUuid: query.classSectionUuid }),
          ...(query.subjectUuid && { subjectUuid: query.subjectUuid }),
          ...(query.status && { status: query.status }),
          ...(query.onDate && { onDate: query.onDate }),
        },
      }),
    ),

  create: (payload: CreateNoticePayload) =>
    unwrap(apiClient.post<ApiSuccess<TeacherNotice>>('/institute/notices', payload)),

  /**
   * Changes a notice already on the board — including publishing a draft and
   * moving a published notice back to one.
   *
   * `PATCH` with only what changed: flipping a notice to a draft sends one
   * field, and does not have to resend the notice to avoid blanking it.
   */
  update: (uuid: string, payload: UpdateNoticePayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<TeacherNotice>>(`/institute/notices/${uuid}`, payload),
    ),

  /** A soft delete: what a class was told on a given day is a term's record. */
  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/notices/${uuid}`)),
};
